import React, { type ReactNode } from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { detailslistitemContract } from './detailslistitem.contract'

const TYPES = detailslistitemContract.props.type.values

export type DetailsListItemType = (typeof TYPES)[number]

export const DETAILS_LIST_ITEM_TEXT = variantForStyleId(detailslistitemContract.textStyleIds[0])

export type DetailsListItemProps = {
    label: ReactNode
    type?: DetailsListItemType
    /** Figma's leading `Vector`, an INSTANCE_SWAP — the caller's glyph. */
    icon?: ReactNode
    /** Figma's `DetailsListItemPart`: the value beside the label. */
    children?: ReactNode
    /** `Action` and `Action 2`, the trailing slots. */
    actions?: ReactNode
    /**
     * The label's ink, when the place using this row overrides it. The
     * definition inks `#label` `content/muted`; `TaskDetailsCard` overrides the
     * instance to `content/default`, and an override belongs to the component
     * applying it.
     */
    labelClassName?: string
    className?: string
    testID?: string
}

/**
 * `type=short` lays label and value on one row; `type=long` stacks them and
 * indents the value by `large-layout/3xl`.
 *
 * The container carries a `FIXED` width from Figma, which is a sample frame
 * rather than a constraint — a definition has nothing to fill. Callers pass
 * `w-full` or their own width through `className`, as `ListItem` does.
 */
const variantFor = (type: DetailsListItemType) => {
    const key = `type=${type}`
    const found = (
        detailslistitemContract.variants as Record<
            string,
            { container: string; label: string; icon: string; parts: Record<string, string> }
        >
    )[key]
    if (!found) {
        throw new Error(`aos-ds DetailsListItem: the Figma contract has no "${key}"`)
    }
    return found
}

export const DetailsListItem = ({
    label,
    type = 'short',
    icon,
    children,
    actions,
    labelClassName,
    className,
    testID,
}: DetailsListItemProps) => {
    const cell = variantFor(type)
    return (
        <View
            className={twMerge('flex-row', cell.container, className)}
            testID={testID ?? `details-list-item-${type}`}
        >
            <View className={twMerge('flex-row', cell.parts.content)}>
                <View className={twMerge('flex-row', cell.parts['details-list-item-part'])}>
                    {icon ? <View className={cell.icon}>{icon}</View> : null}
                    <Typography
                        variant={DETAILS_LIST_ITEM_TEXT}
                        className={twMerge(cell.parts['#label'], labelClassName)}
                        testID='details-list-item-label'
                    >
                        {label}
                    </Typography>
                </View>
                {children ? (
                    <View
                        className={twMerge('flex-row', cell.parts.DetailsListItemPart)}
                        testID='details-list-item-value'
                    >
                        {children}
                    </View>
                ) : null}
            </View>
            {actions ? (
                <View
                    className={twMerge('flex-row', cell.parts.icons)}
                    testID='details-list-item-actions'
                >
                    {actions}
                </View>
            ) : null}
        </View>
    )
}

DetailsListItem.displayName = 'DetailsListItem'

export { TYPES as DETAILS_LIST_ITEM_TYPES }
